import { useContext, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { yupResolver } from '@hookform/resolvers/yup';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChangeHistoryOutlinedIcon from '@mui/icons-material/ChangeHistoryOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  AssignmentOutlined,
  FavoriteBorderOutlined,
  CreateOutlined,
  DeleteOutline,
  Close
} from '@mui/icons-material';
import {
  Box,
  Button,
  CardMedia,
  Chip,
  Divider,
  Grid,
  IconButton,
  MobileStepper,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import halfStar from '../../../components/assets/HalfMaskedStar.svg';
import {
  gray10,
  gray100,
  gray20,
  gray70,
  gray80,
  gray90,
  green50,
  pink,
  primary10,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor,
  primary10ButtonOutlinedColor,
  primaryColor,
  tertiaryButtonOutlinedColor,
  white,
  yellow,
  green10,
  green100
} from '../../../constants/Colors';
import { productStatus, userType } from '../../../constants/Enum';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import {
  certificationFlgOptions,
  productMethodList,
  recommendFlgOptions
} from '../../../constants/SelectOptions';
import { AuthContext } from '../../../contexts/auth';
import { adminProductStatusSchema } from '../../../validation/products';
import {
  AdminProductStatusType,
  ProductDetailType,
  VersionType
} from '../../../validation/products/productType';

import noImage from '../../assets/noImage.svg';
import CustomDialog from '../../uiParts/CustomDialog';
import CustomRadioButton from '../../uiParts/CustomRadioButton';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomPagination from '../../uiParts/CustomPagination';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomTextfield from '../../uiParts/CustomTextfield';
import SideBar from '../../uiParts/SideBar';
import VersionSelect from '../../uiParts/VersionSelect';
import CustomHead from '../CustomHead';
import {
  changeProductStatus,
  adminDeleteProduct,
  getProduct
} from '../../../api/db/product';
import { AxiosError } from 'axios';
import { response } from '../../../api/db';
import { common } from '../../../modules/error';
import { addMylist, deleteMylistProduct } from '../../../api/db/mylist';
import { addFavorite, deleteFavorite } from '../../../api/db/favorite';
import {
  EvaluationGetType,
  EvaluationType
} from '../../../validation/evaluations/evaluationsType';
import {
  adminDeleteReview,
  getAdminProductEvaluations
} from '../../../api/db/evaluation';
import { getOperatorUserList } from '../../../api/db/user';
import { OperatorUserGetType } from '../../../validation/user/userType';
import { sentryLog } from '../../../config/sentry';

const styles = {
  container: {
    '@media screen and (min-width:961px)': {
      pl: sideBarWidth,
      m: '32px'
    },
    '@media screen and (max-width:960px)': {
      mt: spHeaderHeight,
      m: '24px 16px'
    }
  },
  mainImage: {
    width: '100%'
  },
  mainImageContainer: {
    width: '100%',
    position: 'relative'
  },
  subImageContainer: {
    width: '25%'
  },
  backImageButton: {
    position: 'absolute',
    top: '50%',
    left: '16px',
    transform: 'translateY(-50%)',
    color: 'white',
    bgcolor: gray100,
    opacity: '0.6',
    width: '36px',
    '&:hover': {
      bgcolor: gray100,
      opacity: '0.6'
    }
  },
  button: {
    width: '100%',
    fontSize: '16px',
    fontWeight: 500,
    mb: '25px',
    ...primaryButtonBackgroundColor
  },
  nextImageButton: {
    position: 'absolute',
    top: '50%',
    right: '16px',
    transform: 'translateY(-50%)',
    color: 'white',
    bgcolor: gray100,
    opacity: '0.6',
    width: '36px',
    '&:hover': {
      bgcolor: gray100,
      opacity: '0.6'
    }
  },
  aliasText: {
    color: gray100,
    fontSize: '14px',
    '@media screen and (max-width: 960px)': {
      fontSize: '12px'
    }
  },
  categoryText: {
    fontSize: '14px',
    color: gray90,
    '@media screen and (max-width: 960px)': {
      fontSize: '12px'
    }
  },
  nameText: {
    fontSize: '24px',
    color: gray100,
    fontWeight: 700,
    '@media screen and (max-width: 960px)': {
      fontSize: '22px'
    }
  },
  specificationText: {
    fontSize: '18px',
    color: gray100,
    fontWeight: 500,
    ml: '8px',
    mt: '7px',
    '@media screen and (max-width: 960px)': {
      fontSize: '16px'
    }
  },
  summaryText: {
    fontSize: '14px',
    color: gray100,
    mt: '8px'
  },
  unitText: {
    fontSize: '16px',
    color: gray100,
    mr: '8px',
    mt: '10px',
    '@media screen and (max-width: 960px)': {
      fontSize: '14px'
    }
  },
  valueText: {
    fontSize: '28px',
    color: primaryColor,
    fontWeight: 700,
    mr: '4px',
    '@media screen and (max-width: 960px)': {
      fontSize: '24px'
    }
  },
  guideMark: {
    color: gray90,
    cursor: 'pointer',
    height: '24px',
    width: '24px'
  },
  buttonIcon: {
    mr: '8px',
    height: '24px',
    width: '24px'
  },
  buttonText: {
    color: gray100,
    fontSize: '16px',
    fontWeight: 500
  },
  tableItem: {
    bgcolor: gray10,
    width: '30%'
  },
  tableValue: {
    color: gray100
  },
  tableText: {
    '@media screen and (max-width: 600px)': {
      fontSize: '12px'
    }
  },
  basisChip: {
    bgcolor: gray10,
    borderRadius: '0px',
    height: '24px'
  },
  basisActiveChip: {
    color: gray100
  },
  basisInactiveChip: {
    color: gray80
  },
  tableTitleCotainer: {
    p: '24px 0px',
    '@media screen and (max-width: 960px)': {
      p: '17px 0px'
    }
  },
  tableTitle: {
    fontSize: '18px',
    color: gray100,
    fontWeight: 500,
    '@media screen and (max-width: 960px)': {
      fontSize: '16px'
    }
  },
  updatedAt: {
    fontSize: '12px',
    color: gray90
  },
  myListNum: {
    alignItems: 'center',
    color: gray100,
    display: 'flex',
    fontSize: '14px',
    justifyContent: 'center',
    '@media screen and (max-width: 960px)': {
      fontSize: '12px'
    }
  },
  scoreText: {
    alignItems: 'center',
    color: gray100,
    display: 'flex',
    fontSize: '14px',
    justifyContent: 'center',
    '@media screen and (max-width: 960px)': {
      fontSize: '12px'
    }
  },
  halfStar: {
    height: '24px',
    width: '24px'
  },
  recommendChip: {
    bgcolor: primary10,
    color: primaryColor,
    fontSize: '12px',
    border: '1px solid' + primaryColor,
    height: '24px',
    '@media screen and (min-width: 961px)': {
      ml: '16px'
    }
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: primary10,
    height: '42px',
    pl: '15px',
    fontSize: '18px',
    fontWeight: 500,
    color: gray100,
    borderLeft: `3px solid ${primaryColor}`
  },
  noneDisplayOnPC: {
    '@media screen and (min-width: 961px)': {
      display: 'none'
    }
  },
  noneDisplayOnSP: {
    '@media screen and (max-width: 960px)': {
      display: 'none'
    }
  },
  buttonContainer: {
    '@media screen and (min-width:961px)': {
      display: 'none'
    },
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    p: '12px 16px',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'space-between'
  },
  spOutlinedButton: {
    fontSize: '16px',
    p: '10px 16px',
    flexGrow: 1,
    height: '44px',
    ...tertiaryButtonOutlinedColor
  },
  spButton: {
    fontSize: '16px',
    mb: '11px',
    p: '10px 16px',
    ...tertiaryButtonOutlinedColor
  },
  co2ValueIcon: {
    color: primaryColor,
    height: '20px',
    width: '20px',
    verticalAlign: 'bottom'
  },
  co2ValueItem: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '14px',
    mr: '4px',
    verticalAlign: 'bottom'
  },
  co2ValueUnit: {
    color: gray100,
    fontSize: '14px',
    lineHeight: '14px',
    verticalAlign: 'bottom'
  },
  co2Value: {
    color: primaryColor,
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: '26px',
    mr: '4px',
    verticalAlign: 'bottom',
    '@media screen and (max-width: 600px)': {
      fontSize: '24px'
    }
  },
  deleteButton: {
    boxSizing: 'border-box',
    height: '44px',
    p: '10px 22px 10px 16px'
  },
  editButton: {
    boxSizing: 'border-box',
    height: '44px',
    p: '10px 22px 10px 16px',
    '@media screen and (max-width: 900px)': {
      flexGrow: '1'
    }
  },
  draftButton: {
    boxSizing: 'border-box',
    height: '44px',
    p: '10px 22px 10px 16px',
    '@media screen and (max-width: 900px)': {
      flexGrow: '1'
    }
  },
  deleteModal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    maxWidth: '512px',
    minWidth: '294px',
    bgcolor: 'white',
    p: '24px'
  },
  convValueChip: {
    bgcolor: primary10,
    color: primaryColor,
    fontSize: '12px',
    fontWeight: '500',
    height: '21px',
    mr: '8px'
  },
  calcValueChip: {
    bgcolor: green10,
    color: green100,
    fontSize: '12px',
    fontWeight: '500',
    height: '21px',
    mr: '8px'
  }
};

const AdminMaterialDetail = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const isMobile: boolean = useMediaQuery({ query: '(max-width: 900px)' });

  // 画面のアクセス制御
  const { isLoggedIn, setSnackbarOpen, setErrorText, setInfoText } =
    useContext(AuthContext);
  const currentUserType = Number(localStorage.getItem('user_type'));
  useEffect(() => {
    window.scrollTo(0, 0);
    // 未ログイン時はログイン画面に飛ばす
    if (!isLoggedIn) return navigate('/auth/login');
    // 事務局画面で事務局ユーザーではない場合は、ホームに飛ばす
    if (currentUserType !== userType.admin) return navigate('/home');
  }, [isLoggedIn, currentUserType]);

  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [isMylist, setIsMylist] = useState<boolean>(false);
  const [mylistNum, setMylistNum] = useState<number>(0);

  const [operatorUserList, setOperatorUserList] = useState<
    { option: string; value: string }[]
  >([]);

  // 建材取得処理
  const [method, setMethod] = useState<number>(1); // 建材取得時の表示方法の設定
  const [product, setProduct] = useState<ProductDetailType>({
    status: 0,
    alias: '',
    calculatedEmissionIntensity: '',
    calculatedMethod: null,
    calculatedReduceRate: 0,
    calculationBasisFile: {
      name: '',
      path: ''
    },
    calculationBasisName: '',
    calculationBasisType: 0,
    calculationBasisUrl: '',
    carbonOffsetFlg: false,
    calculationSupplement: '',
    category: '',
    condition: '',
    createdAt: '',
    document: {
      name: '',
      path: ''
    },
    documentDescriptionUrl: '',
    emissionIntensity: 0,
    evaluationScore: 0,
    favoriteFlg: 0,
    image: [
      {
        id: 0,
        name: '',
        path: '',
        sort: 0
      }
    ],
    material: '',
    co2Unit: '',
    costUnit: '',
    calculationSpecification: '',
    myListFlg: 0,
    myListNumber: 0,
    name: '',
    otherCertification: '',
    priceHistories: {
      productInfoId: 0,
      settingAt: '',
      referenceUnitPrice: 0,
      unit: ''
    },
    productInfoId: 0,
    recommendFlg: 0,
    reduceRate: 0,
    priceCondition: '',
    referenceUnitPrice: 0,
    relatedUrl: '',
    remark: '',
    settingAt: '',
    priceRemark: '',
    specification: '',
    summary: '',
    updatedAt: '',
    users: {
      company: '',
      companyKana: '',
      companyShort: '',
      department: '',
      email: '',
      firstName: '',
      firstNameKana: '',
      homepage: '',
      industry: '',
      lastName: '',
      lastNameKana: '',
      tel: '',
      certificationFlg: 0,
      certificationName: ''
    },
    productInfos: []
  });

  const location = useLocation();
  const [stateProductInfoId, setStateProductInfoId] = useState<number | null>(
    location.state?.productInfoId || null
  );
  const [productInfoId, setProductInfoId] = useState<number | null>(
    location.state?.oldVersion ?? location.state?.productInfoId ?? null
  );

  // 現在選択されているバージョン
  const [version, setVersion] = useState<number>(0);
  const [versionList, setVersionList] = useState<VersionType[]>([]);

  const dateFormatter = (date: Date, disitFlg?: boolean) => {
    const newDate = new Date(date);
    return newDate.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: disitFlg ? '2-digit' : 'long',
      day: disitFlg ? '2-digit' : 'numeric'
    });
  };

  // 過去バージョンのリスト
  const productInfosArray = (
    latestProductInfoId: number,
    data: Array<{ id: number; created_at: Date }>
  ) => {
    const array: Array<VersionType> = [
      {
        id: latestProductInfoId,
        createdAt: '過去バージョン'
      }
    ];

    if (data.length > 0) {
      data.forEach((productInfo: { id: number; created_at: Date }) => {
        array.push({
          id: productInfo.id,
          createdAt: dateFormatter(productInfo.created_at, true)
        });
      });
    }

    return array;
  };

  // プルダウンからバージョンを選択した時
  const versionChange = (versionId: number) => {
    setProductInfoId(versionId);
    setVersion(versionId);
  };

  const getProductFun = async (
    productId: number,
    productInfoId?: number
  ): Promise<void> => {
    let res;
    productInfoId
      ? (res = await getProduct(productId, method, productInfoId))
      : (res = await getProduct(productId, method));
    const productData = res.data.products;
    const sortedImageData = productData.image.sort(
      (
        a: {
          id: number;
          name: string;
          path: string;
          sort: number;
        },
        b: {
          id: number;
          name: string;
          path: string;
          sort: number;
        }
      ) => ((a && a.sort) || 0) - ((b && b.sort) || 0)
    );
    setProduct({
      status: productData.product_info_status,
      alias: productData.alias,
      calculatedEmissionIntensity: productData.calculated_emission_intensity,
      calculatedMethod: productData.calculated_method,
      calculatedReduceRate: productData.calculated_reduce_rate,
      calculationBasisFile: {
        name: productData.calculation_basis_file.name,
        path: productData.calculation_basis_file.path
      },
      calculationBasisName: productData.calculation_basis_name,
      calculationBasisType: productData.calculation_basis_type,
      calculationBasisUrl: productData.calculation_basis_url,
      carbonOffsetFlg: productData.carbon_offset_flg,
      calculationSupplement: productData.calculation_supplement,
      category: productData.category.name,
      condition: productData.condition,
      createdAt: productData.created_at,
      document: {
        name: productData.document.name,
        path: productData.document.path
      },
      documentDescriptionUrl: productData.document_description_url,
      emissionIntensity: productData.emission_intensity,
      evaluationScore: productData.evaluation_score,
      favoriteFlg: productData.favorite_flg,
      image: sortedImageData,
      material: productData.material.name,
      co2Unit: productData.material.co2_unit,
      costUnit: productData.material.cost_unit,
      calculationSpecification: productData.material.calculation_specification,
      myListFlg: productData.mylist_flg,
      myListNumber: productData.mylist_number,
      name: productData.name,
      otherCertification: productData.other_certification,
      priceHistories: productData.price_histories,
      productInfoId: productData.product_info_id,
      recommendFlg: productData.recommend_flg,
      reduceRate: productData.reduce_rate,
      priceCondition: productData.material.price_condition,
      referenceUnitPrice: productData.reference_unit_price,
      relatedUrl: productData.related_url,
      remark: productData.remark,
      settingAt:
        productData.setting_at && dateFormatter(productData.setting_at),
      priceRemark: productData.price_remarks,
      specification: productData.specification,
      summary: productData.summary,
      updatedAt: dateFormatter(productData.updated_at),
      users: {
        company: productData.users.company,
        companyKana: productData.users.company_kana,
        companyShort: productData.users.company_short,
        department: productData.users.department,
        email: productData.users.email,
        firstName: productData.users.first_name,
        firstNameKana: productData.users.first_name_kana,
        homepage: productData.users.homepage,
        industry: productData.users.industry,
        lastName: productData.users.last_name,
        lastNameKana: productData.users.last_name_kana,
        tel: productData.users.tel,
        certificationFlg: productData.users.certification_flg,
        certificationName: productData.users.certification_name
      }
    });
    setIsFavorite(productData.favorite_flg === 1);
    setIsMylist(productData.mylist_flg === 1);
    setValue('status', productData.product_info_status);
    setValue('rejectReason', productData.reject_reason);
    setValue('recommendFlg', productData.recommend_flg);
    setValue('operatorName', productData.operator_name);
    setValue('operatorMemo', productData.operator_memo);

    // 過去バージョンのセット
    !productInfoId && setStateProductInfoId(productData.product_info_id);
    stateProductInfoId
      ? setVersionList(
          productInfosArray(stateProductInfoId, productData.product_infos)
        )
      : setVersionList(
          productInfosArray(
            productData.product_info_id,
            productData.product_infos
          )
        );
    setVersion(productData.product_info_id);
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        // 事務局 建材取得API
        if (!id) return;
        productInfoId
          ? await getProductFun(Number(id), productInfoId)
          : await getProductFun(Number(id));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(errorResponse.data.message);
            setSnackbarOpen(true);
            setInfoText('');
          }
        } else {
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(common);
            setSnackbarOpen(true);
            setInfoText('');
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id, method, productInfoId]);

  // 建材画像
  let images: string[] = [];
  if (product.image[2]?.path) {
    images = [
      product.image[0]?.path,
      product.image[1]?.path,
      product.image[2]?.path
    ];
  } else if (product.image[1]?.path) {
    images = [product.image[0]?.path, product.image[1]?.path];
  } else if (product.image[0]?.path) {
    images = [product.image[0]?.path];
  } else {
    images = [noImage];
  }
  const [displayMainImageIndex, setDisplayMainImageIndex] = useState(0);

  const handleNextImage = (): void => {
    const newValue = (displayMainImageIndex + 1) % images.length;
    setDisplayMainImageIndex(newValue);
    return;
  };

  const handleBackImage = (): void => {
    let newValue = (displayMainImageIndex - 1) % images.length;
    if (newValue < 0) {
      newValue = 1;
    }
    setDisplayMainImageIndex(newValue);
    return;
  };

  // 建材説明ダイアログ
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleDialogOpen = () => {
    setDialogOpen(!dialogOpen);
  };

  // マイリストボタン
  const MylistButton: React.FC = () => {
    const toggleMylist = async () => {
      try {
        if (!id) return;
        if (isMylist) {
          const res = await deleteMylistProduct(Number(id));
          setIsMylist(false);
          setMylistNum(mylistNum - 1);
          setInfoText &&
            setInfoText(`マイリスト「${res.data.title}」から削除しました`);
          await getProductFun(Number(id), Number(productInfoId));
        } else {
          const res = await addMylist(Number(id));
          setIsMylist(true);
          setMylistNum(mylistNum + 1);
          setInfoText &&
            setInfoText(`マイリスト「${res.data.title}」に追加しました`);
          await getProductFun(Number(id), Number(productInfoId));
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(errorResponse.data.message);
            setInfoText('');
          }
        } else {
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(common);
            setInfoText('');
          }
        }
      } finally {
        setSnackbarOpen && setSnackbarOpen(true);
      }
    };

    return (
      <Button
        sx={[tertiaryButtonOutlinedColor, { height: '44px' }]}
        fullWidth
        onClick={toggleMylist}
      >
        {isMylist ? (
          <>
            <AssignmentIcon
              sx={{
                ...styles.buttonIcon,
                ...{ color: primaryColor }
              }}
            />
            <Typography sx={styles.buttonText}>マイリストに追加済み</Typography>
          </>
        ) : (
          <>
            <AssignmentOutlined
              sx={{
                ...styles.buttonIcon,
                ...{ color: gray90 }
              }}
            />
            <Typography sx={styles.buttonText}>マイリストに追加</Typography>
          </>
        )}
      </Button>
    );
  };

  // お気に入りボタン
  const FavoriteButton: React.FC = () => {
    const toggleFavorite = async () => {
      try {
        if (!id) return;
        if (isFavorite) {
          await deleteFavorite(Number(id));
          setIsFavorite(false);
          setInfoText && setInfoText('お気に入りから削除しました');
        } else {
          await addFavorite(Number(id));
          setIsFavorite(true);
          setInfoText && setInfoText('お気に入りに追加しました');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(errorResponse.data.message);
            setInfoText('');
          }
        } else {
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(common);
            setInfoText('');
          }
        }
      } finally {
        setSnackbarOpen && setSnackbarOpen(true);
      }
    };

    return (
      <>
        <Button
          sx={[
            tertiaryButtonOutlinedColor,
            styles.noneDisplayOnSP,
            { height: '44px' }
          ]}
          fullWidth
          onClick={toggleFavorite}
        >
          {isFavorite ? (
            <>
              <FavoriteOutlinedIcon
                sx={{
                  ...styles.buttonIcon,
                  ...{ color: pink }
                }}
              />
              <Typography sx={styles.buttonText}>
                お気に入りに追加済み
              </Typography>
            </>
          ) : (
            <>
              <FavoriteBorderOutlined
                sx={{
                  ...styles.buttonIcon
                }}
              />
              <Typography sx={styles.buttonText}>お気に入りに追加</Typography>
            </>
          )}
        </Button>
        <IconButton sx={styles.noneDisplayOnPC} onClick={toggleFavorite}>
          {isFavorite ? (
            <FavoriteOutlinedIcon
              sx={{ width: '32px', height: '32px', color: pink }}
            />
          ) : (
            <FavoriteBorderOutlined sx={{ width: '32px', height: '32px' }} />
          )}
        </IconButton>
      </>
    );
  };

  const Rating = (props: { rating: number }) => {
    const { rating } = props;

    const starIcons = new Array(5).fill('outlined').map((_, index) => {
      if (rating - index >= 1) {
        return 'filled';
      } else if (rating - index >= 0.5) {
        return 'half-filled';
      }
      return 'outlined';
    });

    return (
      <Typography
        component="div"
        sx={{
          display: 'flex',
          mr: '4px',
          '@media screen and (max-width: 600px)': {
            height: '20px',
            alignItems: 'center'
          }
        }}
      >
        {starIcons.map((iconType, index) => (
          <Star type={iconType} key={index} />
        ))}
      </Typography>
    );
  };

  type StarProps = {
    type: 'filled' | 'half-filled' | 'outlined';
  };

  const Star = (props: StarProps) => {
    const { type } = props;
    switch (type) {
      case 'filled':
        return (
          <StarOutlinedIcon
            sx={{
              color: yellow,
              '@media screen and (max-width: 600px)': { width: '20px' }
            }}
          />
        );
      case 'half-filled':
        return (
          <CardMedia component="img" image={halfStar} sx={styles.halfStar} />
        );
      case 'outlined':
        return (
          <StarBorderOutlinedIcon
            sx={{
              color: gray80,
              '@media screen and (max-width: 600px)': { width: '20px' }
            }}
          />
        );
      default:
        return (
          <StarBorderOutlinedIcon
            sx={{
              color: gray80,
              '@media screen and (max-width: 600px)': { width: '20px' }
            }}
          />
        );
    }
  };

  // ステータスバッジの色
  function border(): boolean {
    if (product.status == productStatus.draft) {
      return true;
    }
    return false;
  }
  const statusBadgeBorder = { border: border() ? `1px solid ${gray90}` : '' };

  function statusText(): string {
    switch (product.status) {
      case productStatus.approved:
        return '承認済';
      case productStatus.draft:
        return '下書き';
      case productStatus.request:
        return '登録申請中';
      case productStatus.changeRequest:
        return '更新申請中';
      case productStatus.pastVersion:
        return '過去バージョン';
      default:
        return '非承認';
    }
  }

  function badgeColor(): string {
    switch (product.status) {
      case productStatus.approved:
      case productStatus.changeRequest:
        return green50;
      case productStatus.draft:
        return white;
      case productStatus.request:
      case productStatus.pastVersion:
      case productStatus.reject:
      default:
        return gray80;
    }
  }

  // 事務局担当者一覧の取得
  useEffect(() => {
    void (async () => {
      try {
        const res = await getOperatorUserList();
        const users = res.data.users;
        const newDataList = users.map((user: OperatorUserGetType) => {
          return {
            option: `${user.last_name} ${user.first_name}`,
            value: `${user.last_name} ${user.first_name}`
          };
        });
        setOperatorUserList(newDataList);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(errorResponse.data.message);
            setSnackbarOpen(true);
            setInfoText('');
          }
        } else {
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(common);
            setSnackbarOpen(true);
            setInfoText('');
          }
        }
      }
    })();
  }, []);

  // 建材ステータス変更フォーム
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch
  } = useForm<AdminProductStatusType>({
    resolver: yupResolver(adminProductStatusSchema)
  });

  // ステータスリストの作成
  const [statusList, setStatusList] = useState<
    Array<{ disabled: boolean; option: string; value: number }> | []
  >([]);

  useEffect(() => {
    let list: Array<{ disabled: boolean; option: string; value: number }> | [] =
      [];
    switch (product.status) {
      case productStatus.approved:
        list = [
          { disabled: false, option: '承認済', value: productStatus.approved },
          { disabled: false, option: '非承認', value: productStatus.reject }
        ];
        break;
      case productStatus.draft:
        list = [
          { disabled: false, option: '下書き', value: productStatus.draft }
        ];
        break;
      case productStatus.request:
        list = [
          {
            disabled: false,
            option: '登録申請中',
            value: productStatus.request
          },
          { disabled: false, option: '承認', value: productStatus.approved },
          { disabled: false, option: '非承認', value: productStatus.reject }
        ];
        break;
      case productStatus.changeRequest:
        list = [
          {
            disabled: false,
            option: '更新申請中',
            value: productStatus.changeRequest
          },
          { disabled: false, option: '承認', value: productStatus.approved },
          { disabled: false, option: '非承認', value: productStatus.reject }
        ];
        break;
      case productStatus.pastVersion:
        list = [
          {
            disabled: false,
            option: '過去バージョン',
            value: productStatus.pastVersion
          }
        ];
        break;
      case productStatus.reject:
        list = [
          { disabled: false, option: '承認', value: productStatus.approved },
          { disabled: false, option: '非承認', value: productStatus.reject }
        ];
        break;
    }
    setStatusList(list);
  }, [product.status]);

  // 事務局 ステータス変更に関する処理
  const onSubmit = async () => {
    try {
      setLoading(true);
      await changeProductStatus(Number(id), Number(productInfoId), watch);
      if (setInfoText && setSnackbarOpen && setErrorText) {
        setInfoText('保存しました');
        setErrorText('');
        setSnackbarOpen(true);
      }
      // 事務局 建材ステータス変更API
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        if (setErrorText && setSnackbarOpen && setInfoText) {
          setErrorText(errorResponse.data.message);
          setSnackbarOpen(true);
          setInfoText('');
        }
      } else {
        if (setErrorText && setSnackbarOpen && setInfoText) {
          setErrorText(common);
          setSnackbarOpen(true);
          setInfoText('');
        }
      }
    } finally {
      await getProductFun(Number(id), Number(productInfoId));
      setLoading(false);
    }
  };

  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);

  const isButtonDisabled = (): boolean => {
    if (!firstSubmit) return false;
    return (
      watch('status') === null ||
      watch('recommendFlg') === null ||
      Boolean(errors.status) ||
      Boolean(errors.rejectReason) ||
      Boolean(errors.recommendFlg) ||
      Boolean(errors.operatorName) ||
      Boolean(errors.operatorMemo)
    );
  };

  // 削除処理
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [targetForDeletion, setTargetForDeletion] = useState<string>('');
  const [evaluationId, setEvaluationId] = useState<number | undefined>(
    undefined
  );
  const handleDelete = async (text: string, evaluationId?: number) => {
    try {
      setLoading(true);
      if (text === '口コミ') {
        // 口コミの削除
        await adminDeleteReview(Number(evaluationId));
        if (setSnackbarOpen && setInfoText && setErrorText) {
          setSnackbarOpen(true);
          setErrorText('');
          setInfoText('削除しました。');
        }
      } else if (text === '建材') {
        // 建材の削除
        await adminDeleteProduct(Number(id));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        if (setErrorText && setSnackbarOpen && setInfoText) {
          setErrorText(errorResponse.data.message);
          setSnackbarOpen(true);
          setInfoText('');
        }
      } else {
        if (setErrorText && setSnackbarOpen && setInfoText) {
          setErrorText(common);
          setSnackbarOpen(true);
          setInfoText('');
        }
      }
    } finally {
      setOpenDeleteModal(false);
      setLoading(false);
      if (text === '建材') {
        navigate('/operator/product/complete', {
          state: { status: 'delete' }
        });
      }
    }
  };

  // 建材口コミの取得
  const [evaluationList, setEvaluationList] = useState<EvaluationType[]>([]);
  const [operatorEvaluation, setOperatorEvaluation] =
    useState<EvaluationType>();
  const [evaluationCurrentPage, setEvaluationCurrentPage] = useState(1);
  const limit = 20;
  const [totalPages, setTotalPages] = useState(1);
  const topRef = useRef<HTMLDivElement>(null);

  const evaluationDateFormatter = (date: Date) => {
    const newDate = new Date(date);
    return (
      newDate.getFullYear() +
      '/' +
      String(newDate.getMonth() + 1).padStart(2, '0') +
      '/' +
      String(newDate.getDate()).padStart(2, '0')
    );
  };

  // 口コミ最初の20件取得
  useEffect(() => {
    void (async () => {
      try {
        // offsetの設定
        const offset = (evaluationCurrentPage - 1) * limit;
        // 口コミの取得
        const res = await getAdminProductEvaluations(Number(id), limit, offset);
        if (res.data.total_count === 0) return;
        const evaluations = res.data.evaluations;
        // 口コミがない場合、何もしない
        if (!evaluations) return;
        // 口コミがある場合、データを整形してセット
        setTotalPages(Math.ceil(res.data.total_count / limit));

        const operatorEvaluation = evaluations.filter(
          (evaluation: EvaluationGetType) => evaluation.operator_flg === 1
        );
        if (operatorEvaluation.length > 0) {
          setOperatorEvaluation({
            id: operatorEvaluation[0].id,
            score: operatorEvaluation[0].score,
            nickname: operatorEvaluation[0].nickname,
            title: operatorEvaluation[0].title,
            content: operatorEvaluation[0].content,
            operatorFlg: operatorEvaluation[0].operator_flg,
            createdAt: evaluationDateFormatter(
              operatorEvaluation[0].created_at
            ),
            updatedAt: evaluationDateFormatter(operatorEvaluation[0].updated_at)
          });
        }

        const normalEvaluation = evaluations.filter(
          (evaluation: EvaluationGetType) => evaluation.operator_flg === 0
        );
        const newDataList = normalEvaluation.map(
          (evaluation: EvaluationGetType) => {
            return {
              id: evaluation.id,
              score: evaluation.score,
              nickname: evaluation.nickname,
              title: evaluation.title,
              content: evaluation.content,
              operatorFlg: evaluation.operator_flg,
              createdAt: evaluationDateFormatter(evaluation.created_at),
              updatedAt: evaluationDateFormatter(evaluation.updated_at)
            };
          }
        );
        setEvaluationList([...evaluationList, ...newDataList]);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(errorResponse.data.message);
            setSnackbarOpen(true);
            setInfoText('');
          }
        } else {
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(common);
            setSnackbarOpen(true);
            setInfoText('');
          }
        }
      }
    })();
  }, [id]);

  // ページ変更による再取得
  const setListPagination = async (page: number) => {
    try {
      setLoading(true);
      setEvaluationCurrentPage(page);
      const res = await getAdminProductEvaluations(
        Number(id),
        limit,
        (page - 1) * limit
      );
      const evaluations = res.data.evaluations;
      const newDataList = evaluations.map((evaluation: EvaluationGetType) => {
        return {
          id: evaluation.id,
          score: evaluation.score,
          nickname: evaluation.nickname,
          title: evaluation.title,
          content: evaluation.content,
          operatorFlg: evaluation.operator_flg,
          createdAt: evaluationDateFormatter(evaluation.created_at),
          updatedAt: evaluationDateFormatter(evaluation.updated_at)
        };
      });
      setEvaluationList(newDataList);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      if (setErrorText && setSnackbarOpen && setInfoText) {
        setSnackbarOpen(true);
        setErrorText('データの取得に失敗しました。');
        setInfoText('');
      }
    } finally {
      setLoading(false);
    }
  };

  // 下書きに戻す処理
  const handleDraft = async () => {
    try {
      setLoading(true);
      // 事務局 建材ステータス変更API
      await changeProductStatus(Number(id), Number(productInfoId), watch, true);
      if (setInfoText && setSnackbarOpen && setErrorText) {
        setInfoText('下書きに戻しました');
        setErrorText('');
        setSnackbarOpen(true);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        if (setErrorText && setSnackbarOpen && setInfoText) {
          setErrorText(errorResponse.data.message);
          setSnackbarOpen(true);
          setInfoText('');
        }
      } else {
        if (setErrorText && setSnackbarOpen && setInfoText) {
          setErrorText(common);
          setSnackbarOpen(true);
          setInfoText('');
        }
      }
    } finally {
      await getProductFun(Number(id), Number(productInfoId));
      setLoading(false);
    }
  };

  return (
    <>
      <CustomHead title="事務局 建材詳細 | CMCOS[コムコス]" />
      <Grid container>
        <CustomLoading loading={loading} />
        <SideBar />
        <Grid sx={styles.container} container>
          <Box width="100%" sx={styles.noneDisplayOnPC}>
            <CustomSelectField
              onChange={(e) => setMethod(e.target.value as number)}
              value={method}
              list={productMethodList}
              type={'primary'}
            />
          </Box>
          <Grid
            className="imageContainer"
            item
            md={6}
            xs={12}
            sx={{
              pr: '32px',
              '@media screen and (max-width: 960px)': {
                mt: '24px'
              },
              '@media screen and (max-width: 900px)': {
                pr: 0
              }
            }}
          >
            <Grid>
              <Box sx={styles.mainImageContainer}>
                <CardMedia
                  component="img"
                  src={images[displayMainImageIndex]}
                  sx={styles.mainImage}
                />
                {!images[1] ? (
                  <></>
                ) : (
                  <>
                    <IconButton
                      sx={styles.backImageButton}
                      onClick={handleBackImage}
                    >
                      <KeyboardArrowLeft width="24px" height="24px" />
                    </IconButton>
                    <IconButton
                      sx={styles.nextImageButton}
                      onClick={handleNextImage}
                    >
                      <KeyboardArrowRight width="24px" height="24px" />
                    </IconButton>
                  </>
                )}
              </Box>
              <Grid container sx={{ width: '100%' }} justifyContent="center">
                {!images[1] ? (
                  <></>
                ) : (
                  <MobileStepper
                    variant="dots"
                    steps={!images[2] ? 2 : 3}
                    nextButton={null}
                    backButton={null}
                    activeStep={displayMainImageIndex}
                    position="static"
                    sx={[
                      {
                        width: '40px'
                      },
                      styles.noneDisplayOnPC
                    ]}
                  />
                )}
              </Grid>
              {!images[1] ? (
                <></>
              ) : (
                <Grid container mt="8px" sx={styles.noneDisplayOnSP}>
                  <CardMedia
                    component="img"
                    src={images[(displayMainImageIndex + 1) % images.length]}
                    sx={styles.subImageContainer}
                  />
                  {!images[2] ? (
                    <></>
                  ) : (
                    <CardMedia
                      component="img"
                      src={images[(displayMainImageIndex + 2) % images.length]}
                      sx={{ ml: '8px', ...styles.subImageContainer }}
                    />
                  )}
                </Grid>
              )}
            </Grid>
            {!isMobile && (
              <Grid
                sx={{
                  bgcolor: gray20,
                  p: '20px 18px',
                  mt: '52px',
                  width: '100%'
                }}
              >
                <Controller
                  name="status"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      required
                      requiredText="必須"
                      labelText="ステータス"
                      formMb="24px"
                      list={statusList}
                      error={Boolean(errors.status)}
                      helperText={errors.status?.message}
                    />
                  )}
                />
                <Controller
                  name="rejectReason"
                  control={control}
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      labelText="却下理由"
                      multiline
                      rows={2}
                      fieldType="textareaRow2"
                      formMb="40px"
                      error={Boolean(errors.rejectReason)}
                      helperText={errors.rejectReason?.message}
                      width="100%"
                      required={watch('status') === productStatus.reject}
                      requiredText={
                        watch('status') === productStatus.reject ? '必須' : ''
                      }
                    />
                  )}
                />
                <Controller
                  name="recommendFlg"
                  control={control}
                  render={({ field }) => (
                    <CustomRadioButton
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      options={recommendFlgOptions}
                      labelText="事務局おすすめ"
                      requiredText="必須"
                      formMb="24px"
                      error={Boolean(errors.recommendFlg)}
                      helperText={errors.recommendFlg?.message}
                    />
                  )}
                />
                <Controller
                  name="operatorName"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      labelText="事務局担当者"
                      formMb="24px"
                      list={operatorUserList}
                      placeholder="選択してください"
                      error={Boolean(errors.operatorName)}
                      helperText={errors.operatorName?.message}
                    />
                  )}
                />
                <Controller
                  name="operatorMemo"
                  control={control}
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      labelText="事務局メモ"
                      multiline
                      rows={4}
                      fieldType="textareaRow4"
                      formMb="40px"
                      error={Boolean(errors.operatorMemo)}
                      helperText={errors.operatorMemo?.message}
                      width="100%"
                    />
                  )}
                />
                <Button
                  variant="contained"
                  sx={styles.button}
                  onClick={async () => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => {
                      await onSubmit();
                    })();
                  }}
                  disabled={isButtonDisabled()}
                >
                  保存する
                </Button>
              </Grid>
            )}
          </Grid>
          <Grid
            item
            md={6}
            xs={12}
            sx={{
              '@media screen and (max-width: 960px)': {
                mt: '16px'
              }
            }}
          >
            <Grid width="100%" container>
              <Grid
                container
                width="100%"
                height="44px"
                justifyContent="flex-end"
                mb="24px"
                sx={styles.noneDisplayOnSP}
              >
                <Box width="200px">
                  <CustomSelectField
                    onChange={(e) => setMethod(e.target.value as number)}
                    value={method}
                    list={productMethodList}
                    type={'primary'}
                  />
                </Box>
              </Grid>
              <Grid
                container
                flexDirection={'row'}
                justifyContent={'space-between'}
                flexWrap={'nowrap'}
                sx={{
                  mb: '16px',
                  '@media screen and (max-width: 600px)': { mb: '8px' }
                }}
              >
                <Grid item>
                  <Grid alignItems="center" container>
                    <Typography
                      component="span"
                      sx={[
                        {
                          backgroundColor: badgeColor(),
                          borderRadius: '50%',
                          height: '12px',
                          mr: '8px',
                          width: '12px'
                        },
                        statusBadgeBorder
                      ]}
                    ></Typography>
                    <Typography sx={{ fontBold: '500' }}>
                      {statusText()}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid item>
                  <VersionSelect
                    list={versionList ?? []}
                    selectVersion={version}
                    versionChange={versionChange}
                  />
                </Grid>
              </Grid>
              <Grid container justifyContent="space-between">
                <Typography sx={styles.aliasText}>{product.alias}</Typography>
                <Typography sx={styles.categoryText}>
                  {product.category ? product.category : ''}
                  {product.category && product.material ? ' / ' : ''}
                  {product.category && product.material
                    ? product.material.length > 12
                      ? product.material.slice(0, 12) + '...'
                      : product.material
                    : ''}
                </Typography>
              </Grid>
              <Grid container>
                <Typography sx={styles.nameText}>{product.name}</Typography>
                <Typography sx={styles.specificationText}>
                  {product.specification}
                </Typography>
              </Grid>
              <Grid container>
                <Typography sx={styles.summaryText}>
                  {product.summary}
                </Typography>
              </Grid>
              <Grid
                container
                mt="16px"
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item flexGrow={1}>
                  <Grid
                    container
                    flexDirection={'row'}
                    flexWrap={'nowrap'}
                    justifyContent={'space-between'}
                    alignItems={'flex-end'}
                    columnGap={'24px'}
                    mb={'12px'}
                  >
                    <Grid
                      item
                      container
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      columnGap={'4px'}
                      rowGap={'5px'}
                    >
                      <Box>
                        <Typography component="span" sx={styles.co2ValueItem}>
                          CO2原単位
                        </Typography>
                        {product.calculatedMethod === 1 ? (
                          <Chip label="換算値" sx={[styles.convValueChip]} />
                        ) : product.calculatedMethod === 2 ? (
                          <Chip label="算定値" sx={[styles.calcValueChip]} />
                        ) : (
                          <></>
                        )}
                        <Typography component="span" sx={styles.co2Value}>
                          {product.calculatedEmissionIntensity ?? 'ー'}
                        </Typography>
                        <Typography
                          component="span"
                          sx={[styles.co2ValueUnit, { mr: '24px' }]}
                        >
                          kg-CO2/{product.co2Unit}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={styles.co2ValueItem}>
                          CO2削減率
                        </Typography>
                        <ChangeHistoryOutlinedIcon sx={styles.co2ValueIcon} />
                        <Typography component="span" sx={styles.co2Value}>
                          {product.calculatedReduceRate ?? 'ー'}
                        </Typography>
                        <Typography component="span" sx={styles.co2ValueUnit}>
                          %
                        </Typography>
                      </Box>
                    </Grid>
                    <Box ml={'auto'}>
                      <HelpOutlineOutlinedIcon
                        sx={styles.guideMark}
                        onClick={() => {
                          handleDialogOpen();
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              width="100%"
              justifyContent="space-between"
              mt="24px"
              sx={styles.noneDisplayOnSP}
            >
              <Grid item width="63%">
                <MylistButton />
              </Grid>
              <Grid item width="35%">
                <FavoriteButton />
              </Grid>
            </Grid>
            <Grid container width="100%" mt="25px">
              <Grid item width="100%">
                {product.recommendFlg ? (
                  <Chip
                    label="おすすめ"
                    sx={[styles.recommendChip, styles.noneDisplayOnPC]}
                  />
                ) : (
                  <></>
                )}
              </Grid>
              <Typography sx={styles.myListNum}>
                マイリスト追加数：{mylistNum}
              </Typography>
              <Divider
                orientation="vertical"
                sx={{ width: '1px', height: '24px', m: '0 12px' }}
              />
              <Typography sx={styles.scoreText}>
                評価：
                <Rating rating={product.evaluationScore} />
                <Typography component="span" sx={styles.scoreText}>
                  {product.evaluationScore}
                </Typography>
              </Typography>
              {product.recommendFlg ? (
                <Chip
                  label="おすすめ"
                  sx={[styles.recommendChip, styles.noneDisplayOnSP]}
                />
              ) : (
                <></>
              )}
            </Grid>
            {/* material info table */}
            <Grid
              container
              width="100%"
              sx={styles.tableTitleCotainer}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={styles.tableTitle}>建材の情報</Typography>
              <Typography sx={styles.updatedAt}>
                最終更新日：{product.updatedAt}
              </Typography>
            </Grid>
            <TableContainer>
              <Table>
                <TableBody sx={{ border: '1px solid' + gray70 }}>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>適用条件</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.condition}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        メーカー算定値
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.emissionIntensity !== null
                          ? product.emissionIntensity.toLocaleString() + ' '
                          : 'ー '}
                        kg-CO2/{product.co2Unit}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        基準CO2算定仕様
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.calculationSpecification}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>CO2算定根拠</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Grid container flexDirection="column">
                        {/* chips */}
                        <Grid item>
                          <Grid
                            container
                            flexDirection={'row'}
                            gap={'8px'}
                            mb={'4px'}
                          >
                            <Chip
                              icon={
                                product.calculationBasisType === 1 ? (
                                  <CheckOutlinedIcon />
                                ) : undefined
                              }
                              label="第三者認証"
                              sx={{
                                ...styles.basisChip,
                                ...(product.calculationBasisType === 1
                                  ? styles.basisActiveChip
                                  : styles.basisInactiveChip)
                              }}
                            />
                            <Chip
                              icon={
                                product.calculationBasisType === 2 ? (
                                  <CheckOutlinedIcon />
                                ) : undefined
                              }
                              label="自社算定"
                              sx={{
                                ...styles.basisChip,
                                ...(product.calculationBasisType === 2
                                  ? styles.basisActiveChip
                                  : styles.basisInactiveChip)
                              }}
                            />
                            <Chip
                              icon={
                                product.calculationBasisType === 3 ? (
                                  <CheckOutlinedIcon />
                                ) : undefined
                              }
                              label="その他"
                              sx={{
                                ...styles.basisChip,
                                ...(product.calculationBasisType === 3
                                  ? styles.basisActiveChip
                                  : styles.basisInactiveChip)
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Grid item mt={'7px'}>
                          <Typography color={gray100} sx={styles.tableText}>
                            {product.calculationBasisName}
                          </Typography>
                        </Grid>
                        <Grid item mt={'4px'}>
                          <Grid container>
                            <Typography color={gray100} sx={styles.tableText}>
                              ファイル：
                            </Typography>
                            <Box
                              component={'a'}
                              target="_blank"
                              href={product.calculationBasisFile?.path}
                              color={primaryColor}
                              sx={styles.tableText}
                            >
                              {product.calculationBasisFile?.name}
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid item mt={'4px'}>
                          <Grid container>
                            <Typography color={gray100} sx={styles.tableText}>
                              URL：
                            </Typography>
                            <Box
                              component={'a'}
                              target="_blank"
                              href={product.calculationBasisUrl}
                              color={primaryColor}
                              sx={styles.tableText}
                            >
                              {product.calculationBasisUrl}
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        CO2算定の補足
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.calculationSupplement &&
                          (product.calculationSupplement.includes('\n') ? (
                            product.calculationSupplement
                              .split('\n')
                              .map((line, index) => (
                                <span key={index}>
                                  {line}
                                  <br />
                                </span>
                              ))
                          ) : (
                            <span>{product.calculationSupplement}</span>
                          ))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        カーボンオフセットの利用
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.carbonOffsetFlg ? 'あり' : 'なし'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        その他の認定情報
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.otherCertification &&
                          (product.otherCertification.includes('\n') ? (
                            product.otherCertification
                              .split('\n')
                              .map((line, index) => (
                                <span key={index}>
                                  {line}
                                  <br />
                                </span>
                              ))
                          ) : (
                            <span>{product.otherCertification}</span>
                          ))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        補足説明資料
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Grid item mt={'4px'}>
                        <Grid container>
                          <Typography color={gray100} sx={styles.tableText}>
                            ファイル：
                          </Typography>
                          <Box
                            component={'a'}
                            target="_blank"
                            href={product.document?.path}
                            color={primaryColor}
                            sx={styles.tableText}
                          >
                            {product.document?.name}
                          </Box>
                        </Grid>
                        <Grid item mt={'4px'}>
                          <Grid container>
                            <Typography color={gray100} sx={styles.tableText}>
                              URL：
                            </Typography>
                            <Box
                              component={'a'}
                              href={product.documentDescriptionUrl}
                              color={primaryColor}
                              sx={styles.tableText}
                              target="_blank"
                            >
                              {product.documentDescriptionUrl}
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer sx={{ mt: '24px' }}>
              <Table>
                <TableBody sx={{ border: '1px solid' + gray70 }}>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        単価算定条件
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.priceCondition}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        参考設計単価
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {' '}
                        {product.referenceUnitPrice}円/{product.costUnit}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>単価設定日</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.settingAt}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        単価補足説明
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.priceRemark &&
                          (product.priceRemark.includes('\n') ? (
                            product.priceRemark
                              .split('\n')
                              .map((line, index) => (
                                <span key={index}>
                                  {line}
                                  <br />
                                </span>
                              ))
                          ) : (
                            <span>{product.priceRemark}</span>
                          ))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer sx={{ mt: '24px', mb: '24px' }}>
              <Table>
                <TableBody sx={{ border: '1px solid' + gray70 }}>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>関連URL</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Box
                        target="_blank"
                        component={'a'}
                        href={product.relatedUrl}
                        color={primaryColor}
                        sx={styles.tableText}
                      >
                        {product.relatedUrl}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>その他備考</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.remark &&
                          (product.remark.includes('\n') ? (
                            product.remark.split('\n').map((line, index) => (
                              <span key={index}>
                                {line}
                                <br />
                              </span>
                            ))
                          ) : (
                            <span>{product.remark}</span>
                          ))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {/* subscriber info table */}
            <Grid
              container
              width="100%"
              sx={styles.tableTitleCotainer}
              alignItems="center"
            >
              <Typography sx={styles.tableTitle}>登録者の情報</Typography>
            </Grid>
            <TableContainer>
              <Table>
                <TableBody sx={{ border: '1px solid' + gray70 }}>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>会社名</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.users?.company}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>部署名</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.users?.department}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>担当者</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography
                        sx={styles.tableText}
                      >{`${product.users?.lastName} ${product.users?.firstName}`}</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>電話番号</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {' '}
                        {product.users?.tel}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        メールアドレス
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.users?.email}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        企業の環境認定
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.users?.certificationFlg ===
                        certificationFlgOptions[1].value
                          ? '取得済'
                          : product.users?.certificationFlg ===
                            certificationFlgOptions[0].value
                          ? '未取得'
                          : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>認定の名称</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {product.users?.certificationName}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid
            container
            flexDirection={'row'}
            gap={'12px'}
            justifyContent={'flex-end'}
            mt={'24px'}
          >
            {product.status === productStatus.approved &&
              versionList.length == 1 && (
                <Button
                  onClick={handleDraft}
                  startIcon={
                    <AssignmentOutlined sx={{ color: primaryColor }} />
                  }
                  sx={[primary10ButtonOutlinedColor, styles.draftButton]}
                  variant="outlined"
                >
                  下書きに戻す
                </Button>
              )}
            <Button
              onClick={() => {
                navigate(`/operator/product/edit/${id}`, {
                  state: { productInfoId: productInfoId }
                });
              }}
              startIcon={<CreateOutlined sx={{ color: primaryColor }} />}
              sx={[primaryButtonOutlinedColor, styles.editButton]}
              variant="outlined"
            >
              編集する
            </Button>
            {!isMobile ? (
              <Button
                onClick={() => {
                  setTargetForDeletion('建材');
                  setOpenDeleteModal(true);
                }}
                startIcon={<DeleteOutline sx={{ color: gray90 }} />}
                sx={[tertiaryButtonOutlinedColor, styles.deleteButton]}
                variant="outlined"
              >
                削除する
              </Button>
            ) : (
              <IconButton
                onClick={() => {
                  setTargetForDeletion('建材');
                  setOpenDeleteModal(true);
                }}
              >
                <DeleteOutline sx={{ width: '32px', height: '32px' }} />
              </IconButton>
            )}
          </Grid>
        </Grid>

        {isMobile && (
          <Grid
            sx={{
              bgcolor: gray20,
              p: '20px 18px',
              width: '100%'
            }}
          >
            <Controller
              name="status"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectField
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  required
                  requiredText="必須"
                  labelText="ステータス"
                  formMb="24px"
                  list={statusList}
                  error={Boolean(errors.status)}
                  helperText={errors.status?.message}
                />
              )}
            />
            <Controller
              name="rejectReason"
              control={control}
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  labelText="却下理由"
                  multiline
                  rows={2}
                  fieldType="textareaRow2"
                  formMb="40px"
                  error={Boolean(errors.rejectReason)}
                  helperText={errors.rejectReason?.message}
                  width="100%"
                  required={watch('status') === productStatus.reject}
                  requiredText={
                    watch('status') === productStatus.reject ? '必須' : ''
                  }
                />
              )}
            />
            <Controller
              name="recommendFlg"
              control={control}
              render={({ field }) => (
                <CustomRadioButton
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  options={recommendFlgOptions}
                  labelText="事務局おすすめ"
                  requiredText="必須"
                  formMb="24px"
                  error={Boolean(errors.recommendFlg)}
                  helperText={errors.recommendFlg?.message}
                />
              )}
            />
            <Controller
              name="operatorName"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectField
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  labelText="事務局担当者"
                  formMb="24px"
                  list={operatorUserList}
                  placeholder="選択してください"
                  error={Boolean(errors.operatorName)}
                  helperText={errors.operatorName?.message}
                />
              )}
            />
            <Controller
              name="operatorMemo"
              control={control}
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  labelText="事務局メモ"
                  multiline
                  rows={4}
                  fieldType="textareaRow4"
                  formMb="40px"
                  error={Boolean(errors.operatorMemo)}
                  helperText={errors.operatorMemo?.message}
                  width="100%"
                />
              )}
            />
            <Button
              variant="contained"
              sx={styles.button}
              onClick={async () => {
                setFirstSubmit(true);
                await handleSubmit(async () => {
                  await onSubmit();
                })();
              }}
              disabled={isButtonDisabled()}
            >
              保存する
            </Button>
          </Grid>
        )}
        <Grid sx={styles.container} container>
          <Grid item xs={12} id="evaluationSection">
            <Box sx={[styles.heading, { mb: '20px' }]} ref={topRef}>
              建材の評価
            </Box>
            {operatorEvaluation ? (
              <>
                <Box p="24px 16px" sx={{ bgcolor: primary10 }}>
                  <Typography
                    sx={{
                      color: primaryColor,
                      fontSize: '16px',
                      fontWeight: 500
                    }}
                  >
                    事務局口コミ
                  </Typography>
                  <Grid
                    container
                    sx={{ color: gray90, mt: '12px' }}
                    alignItems="center"
                  >
                    <Typography sx={{ fontSize: '14px' }}>
                      {operatorEvaluation.nickname}
                    </Typography>
                    <Divider
                      orientation="vertical"
                      sx={{ width: '1px', height: '16px', m: '0 8px' }}
                    />
                    <Typography sx={{ fontSize: '14px' }}>
                      {operatorEvaluation.updatedAt}
                    </Typography>
                  </Grid>
                  <Grid container sx={{ mt: '4px' }} flexDirection="column">
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '16px',
                        color: gray100
                      }}
                    >
                      {operatorEvaluation.title}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: gray100 }}>
                      {operatorEvaluation.content}
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    flexDirection={'row'}
                    gap={'12px'}
                    justifyContent={'flex-end'}
                    mt={'24px'}
                  >
                    <Button
                      onClick={() => {
                        navigate(`/operator/evaluation/${id}`);
                      }}
                      startIcon={
                        <CreateOutlined sx={{ color: primaryColor }} />
                      }
                      sx={[primaryButtonOutlinedColor, styles.editButton]}
                      variant="outlined"
                    >
                      編集する
                    </Button>
                    {!isMobile ? (
                      <Button
                        onClick={() => {
                          setTargetForDeletion('口コミ');
                          setOpenDeleteModal(true);
                          setEvaluationId(operatorEvaluation.id);
                        }}
                        startIcon={<DeleteOutline sx={{ color: gray90 }} />}
                        sx={[tertiaryButtonOutlinedColor, styles.deleteButton]}
                        variant="outlined"
                      >
                        削除する
                      </Button>
                    ) : (
                      <IconButton
                        onClick={() => {
                          setTargetForDeletion('口コミ');
                          setOpenDeleteModal(true);
                          setEvaluationId(operatorEvaluation.id);
                        }}
                      >
                        <DeleteOutline sx={{ width: '32px', height: '32px' }} />
                      </IconButton>
                    )}
                  </Grid>
                </Box>
                <Divider sx={{ width: '100%' }} />
              </>
            ) : (
              <Box p="24px 16px" sx={{ bgcolor: primary10 }}>
                <Typography sx={{ color: gray90, mb: '20px' }}>
                  この建材の事務局口コミはまだ書いていません
                </Typography>
                {product.status === productStatus.approved && (
                  <Button
                    onClick={() => {
                      navigate(`/operator/evaluation/${id}`);
                    }}
                    startIcon={<CreateOutlined sx={{ color: white }} />}
                    sx={[primaryButtonBackgroundColor, styles.editButton]}
                    variant="contained"
                  >
                    口コミを書く
                  </Button>
                )}
              </Box>
            )}
            {evaluationList.length > 0 ? (
              evaluationList.map((evaluation, index) => {
                return (
                  <Grid key={index}>
                    <Box p="24px 16px">
                      <Grid container alignItems="center">
                        <Rating rating={evaluation.score} />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: gray100,
                            fontWeight: 500,
                            ml: '4px'
                          }}
                        >
                          {evaluation.score}
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        sx={{ color: gray90, mt: '4px' }}
                        alignItems="center"
                      >
                        <Typography sx={{ fontSize: '14px' }}>
                          {evaluation.nickname}
                        </Typography>
                        <Divider
                          orientation="vertical"
                          sx={{ width: '1px', height: '16px', m: '0 8px' }}
                        />
                        <Typography sx={{ fontSize: '14px' }}>
                          {evaluation.updatedAt}
                        </Typography>
                      </Grid>
                      <Grid container sx={{ mt: '4px' }} flexDirection="column">
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: '16px',
                            color: gray100
                          }}
                        >
                          {evaluation.title}
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: gray100 }}>
                          {evaluation.content}
                        </Typography>
                      </Grid>
                    </Box>
                    <Divider sx={{ width: '100%' }} />
                  </Grid>
                );
              })
            ) : (
              <Grid
                sx={{
                  m: '80px',
                  '@media screen and (max-width:961px)': {
                    m: '80px 0 120px'
                  }
                }}
              >
                <Typography
                  sx={{ color: gray90, fontSize: '16px', textAlign: 'center' }}
                >
                  評価コメントはまだありません
                </Typography>
              </Grid>
            )}
            {totalPages > 1 && (
              <CustomPagination
                currentPage={evaluationCurrentPage}
                totalPages={totalPages}
                handlePageChange={(e, page) => {
                  setListPagination(page);
                  topRef?.current?.scrollIntoView();
                }}
              />
            )}
          </Grid>
        </Grid>
        <Grid sx={styles.buttonContainer} container flexDirection="row">
          <Box sx={{ width: 'calc(100% - 48px)' }}>
            <MylistButton />
          </Box>
          <FavoriteButton />
        </Grid>
        <CustomDialog
          onClose={() => {
            handleDialogOpen();
          }}
          open={dialogOpen}
        />
        <Modal
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          sx={{ m: '16px' }}
        >
          <Box sx={styles.deleteModal}>
            <Box
              sx={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                width: '24px',
                height: '24px',
                color: gray90
              }}
            >
              <Close
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setOpenDeleteModal(false);
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 500,
                color: gray100,
                mb: '24px'
              }}
            >
              {targetForDeletion}を削除
            </Typography>
            <Typography sx={{ fontSize: '16px', color: gray100, mb: '40px' }}>
              削除すると元に戻すことはできません。よろしいですか？
            </Typography>
            <Grid
              container
              sx={{ width: '100%' }}
              justifyContent="space-between"
            >
              <Button
                fullWidth
                variant="outlined"
                sx={{
                  ...tertiaryButtonOutlinedColor,
                  fontSize: '16px',
                  height: '43px',
                  width: '50%',
                  maxWidth: '248px',
                  '@media screen and (max-width:600px)': {
                    maxWidth: '140px'
                  }
                }}
                onClick={() => {
                  setOpenDeleteModal(false);
                }}
              >
                キャンセル
              </Button>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  ...primaryButtonBackgroundColor,
                  fontSize: '16px',
                  height: '43px',
                  width: '50%',
                  maxWidth: '248px',
                  '@media screen and (max-width:600px)': {
                    maxWidth: '140px'
                  }
                }}
                onClick={() => {
                  handleDelete(targetForDeletion, Number(evaluationId));
                }}
              >
                削除する
              </Button>
            </Grid>
          </Box>
        </Modal>
      </Grid>
    </>
  );
};

export default AdminMaterialDetail;
